import React, { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FaPlug } from "react-icons/fa";
import { lightspeedService } from "../../../services/lightspeedService";
import {
  LightspeedError,
  handleLightspeedError,
} from "../../utils/lightspeedErrorHandler";
import { LightspeedConnectButtonProps } from "../../types/lightspeed.types";
import { getUserIdFromStorage } from "../../utils";
import "./lightspeed-components.scss";
import { DomainPrefixModal } from "./DomainPrefixModal";

export const LightspeedConnectButton: React.FC<
  LightspeedConnectButtonProps
> = ({
  storeId,
  onSuccess,
  onError,
  className = "",
  variant = "primary",
  size,
  children = "Connect to Lightspeed",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lightspeedError, setLightspeedError] =
    useState<LightspeedError | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [defaultInput, setDefaultInput] = useState("");

  const handleConnect = () => {
    setLightspeedError(null);
    setDefaultInput("");
    setShowModal(true);
  };

  const handleSubmitDomainPrefix = async (domainPrefix: string) => {
    if (!storeId) {
      const error = new Error("Store ID is required to connect");
      const lightspeedErr = handleLightspeedError(error);
      setLightspeedError(lightspeedErr);
      onError?.(lightspeedErr);
      return;
    }

    setIsLoading(true);
    setShowModal(false);
    try {
      const userId = getUserIdFromStorage();
      const TAB_NAME = "lightspeed_oauth_tab";
      let pendingTab: Window | null = null;
      try {
        pendingTab = window.open("about:blank", TAB_NAME);
        if (pendingTab && pendingTab.document) {
          try {
            pendingTab.document
              .write(`<!doctype html><html><head><meta charset="utf-8" />
              <title>Connecting to Lightspeed…</title>
              <style>
                html,body{height:100%;margin:0}
                .center{display:flex;align-items:center;justify-content:center;height:100%;font-family:system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica, Arial, sans-serif;color:#222}
                .pill{padding:.625rem 1rem;border:1px solid #ddd;border-radius:999px;background:#fafafa}
              </style>
            </head><body>
              <div class="center"><div class="pill">Opening Lightspeed …</div></div>
            </body></html>`);
            pendingTab.document.close();
            pendingTab.focus();
          } catch (writeErr) {}
        }
      } catch (e) {}
      const response = await lightspeedService.startOAuth(
        storeId,
        userId || undefined,
        domainPrefix
      );
      
      if (response.success && response.data) {
        const sessionId = (response.data as any).sessionId as
          | string
          | undefined;
        if (sessionId) {
          try {
            sessionStorage.setItem("lightspeed_oauth_session_id", sessionId);
          } catch (storageErr) {}
        }
        const rawUrl = (response.data as any).authorizationUrl as
          | string
          | undefined;
        const authUrl =
          typeof rawUrl === "string"
            ? rawUrl
                .trim()
                .replace(/^`+|`+$/g, "")
                .replace(/^"+|"+$/g, "")
            : undefined;
        if (authUrl) {
          let tabOpened = false;
          if (pendingTab) {
            try {
              pendingTab.location.replace(authUrl);
              tabOpened = true;
            } catch (e) {
              const newTab = window.open(authUrl, TAB_NAME);
              tabOpened = !!newTab;
            }
          } else {
            const newTab = window.open(authUrl, TAB_NAME);
            tabOpened = !!newTab;
          }

          if (!tabOpened) {
            throw new Error(
              "Failed to open authorization window. Please allow popups for this site and try again."
            );
          }
        } else {
          throw new Error("No authorization URL received from server");
        }
        onSuccess?.();
      } else {
        throw new Error(response.error ?? "Failed to start OAuth flow");
      }
    } catch (err) {
      const lightspeedErr = handleLightspeedError(err);
      setLightspeedError(lightspeedErr);
      onError?.(lightspeedErr);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`lightspeed-connect ${className}`}>
      <Button
        variant={variant}
        onClick={handleConnect}
        disabled={isLoading}
        size={size}
      >
        {isLoading ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Connecting...
          </>
        ) : (
          <>
            <FaPlug className="me-2" />
            {children}
          </>
        )}
      </Button>
      <DomainPrefixModal
        show={showModal}
        onCancel={() => setShowModal(false)}
        onSubmit={handleSubmitDomainPrefix}
        defaultValue={defaultInput}
      />
      {lightspeedError && (
        <div className="error-message">
          <small>{lightspeedError.userMessage}</small>
        </div>
      )}
    </div>
  );
};

export default LightspeedConnectButton;
